import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { WardComponent } from './ward/ward.component';
import { RoomTypeComponent } from './room-type/room-type.component';
import { RoomComponent } from './room/room.component';
import { BedComponent } from './bed/bed.component';
import { IWard } from './ward/Iward';
import { IRoomType } from './room-type/iroomtype';
import { IRoom } from './room/iroom';
import { Subject, takeUntil } from 'rxjs';

type NavigationLevel = 'ward' | 'roomtype' | 'room' | 'bed';

@Component({
  selector: 'app-ipd',
  imports: [
    CommonModule,
    RouterModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    WardComponent,
    RoomTypeComponent,
    RoomComponent,
    BedComponent
  ],
  templateUrl: './ipdTab.component.html',
  styleUrl: './ipdTab.component.scss'
})
export class IpdTabComponent implements OnInit, OnDestroy {
  currentLevel: NavigationLevel = 'ward';
  selectedWard: IWard | null = null;
  selectedRoomType: IRoomType | null = null;
  selectedRoom: IRoom | null = null;
  breadcrumbData: any = {
    title: 'ENTITY.WARD',
    urls: [
      { title: 'SETTING', url: '/settings' },
      { title: 'ENTITY.WARD' }
    ]
  };

  private _unsubscribeAll: Subject<void> = new Subject<void>();
  wardId: number | null = null;
  roomTypeId: number | null = null;
  roomId: number | null = null;

  constructor(
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(params => {
        this.wardId = params['wardId'] ? +params['wardId'] : null;
        this.roomTypeId = params['roomTypeId'] ? +params['roomTypeId'] : null;
        this.roomId = params['roomId'] ? +params['roomId'] : null;
        this.determineCurrentLevel();
        this.updateBreadcrumb();
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private determineCurrentLevel(): void {
    if (this.roomId) {
      this.currentLevel = 'bed';
    } else if (this.roomTypeId) {
      this.currentLevel = 'room';
    } else if (this.wardId) {
      this.currentLevel = 'roomtype';
    } else {
      this.currentLevel = 'ward';
    }
  }

  onWardClick(ward: IWard): void {
    this.router.navigate(['/ipd/ward', ward.id]);
  }

  onRoomTypeClick(roomType: IRoomType): void {
    if (this.wardId) {
      this.router.navigate(['/ipd/ward', this.wardId, 'Roomtype', roomType.id]);
    }
  }

  onRoomClick(room: IRoom): void {
    if (this.wardId && this.roomTypeId) {
      this.router.navigate(['/ipd/ward', this.wardId, 'Roomtype', this.roomTypeId, 'Room', room.id]);
    }
  }

  resetNavigation(): void {
    this.router.navigate(['/ipd']);
  }

  private updateBreadcrumb(): void {
    const urls: any[] = [{ title: 'SETTING', url: '/settings' }];
    let breadcrumbTitle = 'ENTITY.WARD';

    switch (this.currentLevel) {
      case 'ward':
        urls.push({ title: 'ENTITY.WARD' });
        break;
      case 'roomtype':
        breadcrumbTitle = 'ENTITY.ROOM_TYPE';
        urls.push({ title: 'ENTITY.WARD', url: '/ipd' });
        urls.push({ title: 'ENTITY.ROOM_TYPE' });
        break;
      case 'room':
        breadcrumbTitle = 'ENTITY.ROOM';
        urls.push({ title: 'ENTITY.WARD', url: '/ipd' });
        urls.push({ title: 'ENTITY.ROOM_TYPE', url: this.wardId ? `/ipd/ward/${this.wardId}` : '/ipd' });
        urls.push({ title: 'ENTITY.ROOM' });
        break;
      case 'bed':
        breadcrumbTitle = 'ENTITY.BED';
        urls.push({ title: 'ENTITY.WARD', url: '/ipd' });
        urls.push({ title: 'ENTITY.ROOM_TYPE', url: this.wardId ? `/ipd/ward/${this.wardId}` : '/ipd' });
        urls.push({ title: 'ENTITY.ROOM', url: (this.wardId && this.roomTypeId) ? `/ipd/ward/${this.wardId}/Roomtype/${this.roomTypeId}` : '/ipd' });
        urls.push({ title: 'ENTITY.BED' });
        break;
    }

    this.breadcrumbData = {
      title: breadcrumbTitle,
      urls: urls
    };
  }
}
